<template>
  <div>
    <div style="text-align: center"><v-chip class="ma-2" color="secondary" small v-if="questionresponse.example">{{$t('general.example')}}</v-chip></div>
    <component :is="componentName"
               v-if="$options.components[componentName]"
               :journeyresponse="journeyresponse"
               :value="answerDraft"
               :visible="visible"
               @input="answerInput($event)"
               v-show="!showUploader"
    ></component>
    <div v-else>
      Not supported.
      {{questionresponse}}
    </div>
    <VideoUploading v-if="showUploader" @complete="uploadComplete()" @cancel="cancelUpload()"></VideoUploading>
    <BrowserProctoringMonitor v-if="proctoringMonitorActive" @issue="proctoringIssueTriggered()"></BrowserProctoringMonitor>
  </div>
</template>

<script>
import VideoUploading from "./VideoUploading";
import LikertResponse from "./responses/LikertResponse";
import VideoResponse from "./responses/VideoResponse";
import CogdiffResponse from "./responses/CogdiffResponse";
import OptionResponse from "./responses/OptionResponse";
import TextAreaResponse from "./responses/TextAreaResponse";
import TextResponse from "./responses/TextResponse";
import IpsativeResponse from "./responses/IpsativeResponse";
import EmailResponse from "./responses/EmailResponse";
import TextList from "./responses/TextListResponse";
import RankResponse from "./responses/RankResponse";
import applicationEventHub from "../../../../events/applicationEventHub";
import soundController from "../../../../lib/soundController";
import BrowserProctoringMonitor from "./BrowserProctoringMonitor.vue";

export default {
  name: "QuestionresponseRenderer",
  props:{
    journeyresponse:{required:true, type:Object},
    visible:{type:Boolean, default:true}
  },
  computed:{
    questionresponse(){
      return this.journeyresponse.questionresponse;
    },
    answer(){
      if(!this.$store.state.assessment.currentAnswers){
        return null;
      }
      return this.$store.state.assessment.currentAnswers[this.journeyresponse.journeyresponseid];
    },
    componentName(){
      return 'response-'+this.questionresponse.response_type;
    },
    shouldAutoContinue(){
      return !this.questionresponse.example && this.autoContinue.includes(this.questionresponse.response_type);
    },
    endReached(){
      return this.$store.getters['assessment/currentMeasureIsAtEnd'];
    },
    answerSecondsLeft(){
      if(!this.answer){
        return null;
      }

      if(this.answer.secs_left !== undefined && this.answer.secs_left !== null){
        return this.answer.secs_left;
      }

      return null;
    }
  },
  components:{
    BrowserProctoringMonitor,
    'response-ips_most_least':IpsativeResponse,
    'response-email':EmailResponse,
    'response-likert':LikertResponse,
    'response-video':VideoResponse,
    'response-cogdiff':CogdiffResponse,
    'response-options':OptionResponse,
    'response-textarea':TextAreaResponse,
    'response-text':TextResponse,
    'response-text_list':TextList,
    'response-textarea_list':TextList,
    'response-rank':RankResponse,
    VideoUploading
  },
  data(){
    return {
      autoContinue:['likert','cogdiff','options'],
      answerDraft:null,
      showUploader:false,
      internalSecondsLeft : null,
      proctoringMonitorActive: false,
    };
  },
  mounted(){
    this.cloneAnswer();
    this.$watch('answer',()=>{
      this.cloneAnswer();
    },{deep:true});

    this.bindEvents();
    this.beginCountdown();
  },
  methods:{
    bindEvents(){
      applicationEventHub.$on('journey.next',this.onJourneyNext);
      applicationEventHub.$on('journey.back',this.onJourneyBack);
    },
    unbindEvents(){
      applicationEventHub.$off('journey.next',this.onJourneyNext);
      applicationEventHub.$off('journey.back',this.onJourneyBack);
    },
    onJourneyBack(){
      this.saveDraftAnswer().then(()=>{
        this.previousQuestion();
      });
    },
    onJourneyNext(){
      this.saveDraftAnswer().then(()=>{
        this.validateAnswer().then(()=>{
          this.nextQuestion();
        }).catch(()=>{
          //Validation failed
          soundController.beep();
        });
      });
    },
    cloneAnswer(){
      this.answerDraft=JSON.parse(JSON.stringify(this.answer));
    },
    answerInput(answer){
      this.answerDraft=this.processedAnswer(answer);
      if(this.answerDraft.complete && this.shouldAutoContinue){
        this.onJourneyNext();
      }else if(this.responseHasVideoAnswer(this.answerDraft)){
        this.saveDraftAnswer();
      }else if(this.questionresponse.response_type==='rank'){
        this.saveDraftAnswer();
      }
    },
    nextQuestion(){
      if(this.endReached){
        //TODO: Ensure everything was answered
        this.emitComplete();
      }else{
        this.$store.dispatch('assessment/nextQuestion');
      }
    },
    emitComplete(){
      this.$emit('complete',this.journeyresponse);
    },
    previousQuestion(){
      this.$store.dispatch('assessment/previousQuestion');
    },
    saveDraftAnswer(){
      return this.$store.dispatch('assessment/saveAnswer',this.processedAnswer(this.answerDraft));
    },
    processedAnswer(answer){
      if(answer){
        let complete=true;
        for(let journeyanswer of this.journeyresponse.journeyanswers){
          const fieldAnswer=answer.answers[journeyanswer.journeyanswerid];
          if(fieldAnswer.type==='video'){
            if(journeyanswer.field.required && (!fieldAnswer.responsevideos || fieldAnswer.responsevideos.length===0)){
              complete=false;
            }
          }else{
            if(journeyanswer.field.required && (fieldAnswer.value===null || fieldAnswer.value==='' || fieldAnswer.value===undefined)){
              complete=false;
            }
          }
        }
        answer.secs_left=this.internalSecondsLeft;
        if(answer.secs_left===0){
          complete=true;
        }
        answer.complete=complete;
      }
      return answer;
    },
    validateAnswer(){
      return new Promise(async (resolve,reject)=>{
        const result=this.applyValidationRules();
        if(result===true){
          this.$store.dispatch('assessment/clearValidation').then(()=>{
            resolve();
          });
        }else{
          this.$store.dispatch('assessment/validationFailed',{message:result}).then(()=>{
            reject();
          });
        }
      });
    },
    applyValidationRules(){
      if(this.questionresponse.required){
        if(!this.answerDraft.complete){
          return 'validation.required';
        }
      }
      return true;
    },
    responseHasVideoAnswer(response){
      if(response && response.answers){
        for(let id of Object.keys(response.answers)){
          const ans=response.answers[id];
          if(ans.type==='video' && ans.responsevideos && ans.responsevideos.length > 0){
            return true;
          }
        }
      }
      return false;
    },
    uploadComplete(){
      this.showUploader=false;
      this.onJourneyNext();
    },
    cancelUpload(){
      this.showUploader=false;
    },
    beginCountdown(){
      if(this.answerSecondsLeft === null){
        return;
      }
      this.internalSecondsLeft = this.answerSecondsLeft;
      this.countdownActive();

      if(this.internalSecondsLeft > 0){
        this._timeLimitInterval = setInterval(() => {
          if(this.internalSecondsLeft > 0){
            this.internalSecondsLeft--;
            this.saveDraftAnswer();
            this.countdownActive();
          }else{
            this.saveDraftAnswer();
            this.emitTimeReached();

            this.countdownInactive();
          }
        },1000);
      }else{
        this.saveDraftAnswer();
        this.emitTimeReached();
        applicationEventHub.hideQuestionTimer();
        this.countdownInactive();
      }
    },
    countdownActive() {
      applicationEventHub.showQuestionTimer(this.internalSecondsLeft);
      this.proctoringMonitorActive=true;
    },
    countdownInactive() {
      applicationEventHub.hideQuestionTimer();
      clearInterval(this._timeLimitInterval);
      this.proctoringMonitorActive=false;
    },
    cleanTimerEvents() {
      this.proctoringMonitorActive=false;
      clearInterval(this._timeLimitInterval);
      applicationEventHub.hideQuestionTimer();
    },
    emitTimeReached() {
      this.$emit('time-reached', this.journeyresponse);
    },
    proctoringIssueTriggered() {
      this.internalSecondsLeft=0;
      this.saveDraftAnswer();
      this.emitTimeReached();
      this.cleanTimerEvents();
    }
  },
  beforeDestroy() {
    this.unbindEvents();
    this.cleanTimerEvents()
  }
}
</script>

<style scoped>

</style>